import React from 'react'
import PropTypes from 'prop-types'
import {Input, Select} from 'antd'
import {DragDropContext, Droppable, Draggable} from 'react-beautiful-dnd'
import {MAX_DESCRIPTION, getMaterialLabel} from './constants'

const {Option} = Select

/** Icon thùng rác vẽ thẳng bằng SVG — không phụ thuộc font icomoon, luôn ăn màu qua currentColor. */
const TrashIcon = () => (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="3 6 5 6 21 6"/>
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
        <line x1="10" y1="11" x2="10" y2="17"/>
        <line x1="14" y1="11" x2="14" y2="17"/>
    </svg>
)

class LayerTable extends React.Component {
    _handleDragEnd = (result) => {
        const {onReorder} = this.props
        const {source, destination} = Object.assign({}, result)

        if (!destination || source.index === destination.index) return

        onReorder(source.index, destination.index)
    }

    _handleFilterOption = (input, option) => {
        const keyword = (input || '').trim().toLowerCase()

        if (!keyword) return true

        const label = (option && (option.children || (option.props && option.props.children))) || ''

        return `${label}`.toLowerCase().includes(keyword)
    }

    /**
     * Danh mục NVL như BE trả về, cộng thêm mã mà layer đang trỏ tới nếu mã đó
     * không còn trong danh mục, để giá trị hiện tại vẫn hiện tên thay vì trơ mã.
     */
    _getMaterialOptions = (layer) => {
        const {materials} = this.props

        if (!layer.material_code || materials.some(material => material.code === layer.material_code)) return materials

        return [...materials, {code: layer.material_code, name: layer.material_name}]
    }

    _renderRow = (layer, index) => {
        const {
            materials, materialsLoaded, errors, disabled, focusKey,
            onChangeMaterial, onChangeDescription, onChangeFlag, onRequestDelete,
        } = this.props
        // Dòng vừa thêm là dòng duy nhất mount mới -> autoFocus/defaultOpen chỉ chạy đúng một lần cho nó.
        const isNewRow = !!focusKey && layer.key === focusKey
        const selectedMaterial = materials.find(material => material.code === layer.material_code)
        // Danh mục chưa tải xong thì chưa kết luận được -> không gắn nhãn cảnh báo sai cho mọi dòng.
        const isUnknown = materialsLoaded && !!layer.material_code && !selectedMaterial
        const error = errors[layer.key] || {}
        // Số layer dựng từ index đang render, nên kéo thả hoặc xoá dòng thì message vẫn khớp cột #.
        const messages = [
            error.material && `Layer ${index + 1} chưa chọn nguyên vật liệu`,
            error.flags && `Layer ${index + 1} phải được đánh dấu In hoặc Cắt`,
        ].filter(Boolean).join('. ')
        // Mockup bôi đỏ ô Nguyên vật liệu cho mọi lỗi của dòng, kể cả khi chỉ sai cờ In/Cắt,
        // vì message lỗi của cả dòng hiển thị ngay dưới ô này.
        const hasError = !!messages

        return (
            <Draggable key={layer.key} draggableId={`layer-${layer.key}`} index={index} isDragDisabled={disabled}>
                {(provided, snapshot) => (
                    <tr
                        className={snapshot.isDragging ? 'is-dragging' : ''}
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                    >
                        <td className="ps-col-grip">
                            <span
                                className="ps-grip"
                                title={`Giữ và kéo để đổi thứ tự layer ${index + 1}`}
                                {...provided.dragHandleProps}
                            >
                                ⣿
                            </span>
                        </td>
                        <td className="ps-col-no">{index + 1}</td>
                        <td className={`ps-td-material ${hasError ? 'is-err' : ''}`}>
                            <Select
                                className="ps-select"
                                showSearch
                                allowClear
                                loading={!materialsLoaded}
                                notFoundContent={materialsLoaded ? undefined : 'Đang tải danh mục nguyên vật liệu…'}
                                autoFocus={isNewRow}
                                defaultOpen={isNewRow}
                                disabled={disabled}
                                value={layer.material_code || undefined}
                                placeholder="Chọn nguyên vật liệu"
                                optionFilterProp="children"
                                filterOption={this._handleFilterOption}
                                onChange={value => onChangeMaterial(layer.key, value || '')}
                            >
                                {this._getMaterialOptions(layer).map(material => (
                                    <Option key={material._id || material.code} value={material.code}>
                                        {getMaterialLabel(material)}
                                    </Option>
                                ))}
                            </Select>
                            {isUnknown && <span className="ps-warnbadge">NVL không còn trong danh mục</span>}
                            {!!messages && <div className="ps-msg">{messages}</div>}
                        </td>
                        <td className="ps-col-desc">
                            <Input
                                className="ps-desc-input"
                                allowClear
                                maxLength={MAX_DESCRIPTION}
                                disabled={disabled}
                                value={layer.description}
                                placeholder="Mô tả (không bắt buộc)"
                                aria-label={`Mô tả layer ${index + 1}`}
                                onChange={e => onChangeDescription(layer.key, e.target.value)}
                            />
                        </td>
                        <td className={`ps-col-flag ${error.flags ? 'is-err' : ''}`}>
                            <input
                                type="checkbox"
                                disabled={disabled}
                                checked={layer.is_print}
                                aria-label={`Layer ${index + 1} có in`}
                                onChange={e => onChangeFlag(layer.key, 'is_print', e.target.checked)}
                            />
                        </td>
                        <td className={`ps-col-flag ${error.flags ? 'is-err' : ''}`}>
                            <input
                                type="checkbox"
                                disabled={disabled}
                                checked={layer.is_cut}
                                aria-label={`Layer ${index + 1} có cắt`}
                                onChange={e => onChangeFlag(layer.key, 'is_cut', e.target.checked)}
                            />
                        </td>
                        <td className="ps-col-act">
                            <button
                                type="button"
                                className="ps-icon-del"
                                disabled={disabled}
                                aria-label={`Xoá layer ${index + 1}`}
                                onClick={() => onRequestDelete(index)}
                            >
                                <TrashIcon/>
                            </button>
                        </td>
                    </tr>
                )}
            </Draggable>
        )
    }

    render() {
        const {layers, disabled, onAdd} = this.props

        if (layers.length === 0) {
            return (
                <div className="ps-empty">
                    <div className="ps-empty-icon">▤</div>
                    <p>Chưa cấu hình layer nào cho SKU này</p>
                    <button type="button" className="ps-btn ps-btn-primary" disabled={disabled} onClick={onAdd}>
                        + Thêm layer
                    </button>
                </div>
            )
        }

        return (
            <DragDropContext onDragEnd={this._handleDragEnd}>
                <Droppable droppableId="layer-list">
                    {provided => (
                        <div className="ps-table-scroll">
                            <table className="ps-table">
                                <thead>
                                <tr>
                                    <th className="ps-col-grip"/>
                                    <th className="ps-col-no">#</th>
                                    <th>Nguyên vật liệu</th>
                                    <th className="ps-col-desc">Mô tả</th>
                                    <th className="ps-col-flag">In</th>
                                    <th className="ps-col-flag">Cắt</th>
                                    <th className="ps-col-act">Hành động</th>
                                </tr>
                                </thead>
                                <tbody ref={provided.innerRef} {...provided.droppableProps}>
                                {layers.map(this._renderRow)}
                                {provided.placeholder}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        )
    }
}

LayerTable.defaultProps = {
    layers: [],
    materials: [],
    materialsLoaded: false,
    errors: {},
    disabled: false,
}

LayerTable.propTypes = {
    layers: PropTypes.array.isRequired,
    materials: PropTypes.array,
    materialsLoaded: PropTypes.bool,
    errors: PropTypes.object,
    disabled: PropTypes.bool,
    focusKey: PropTypes.string,
    onAdd: PropTypes.func.isRequired,
    onChangeMaterial: PropTypes.func.isRequired,
    onChangeDescription: PropTypes.func.isRequired,
    onChangeFlag: PropTypes.func.isRequired,
    onRequestDelete: PropTypes.func.isRequired,
    onReorder: PropTypes.func.isRequired,
}

export default LayerTable
